import React, { useState, useEffect, useRef } from 'react';
import { PLAYGROUND_PRESETS } from '../../data/content';
import { DoubleBezelCard } from '../common/DoubleBezelCard';
import { PixelGlyph } from '../common/PixelGlyph';
import { MagneticButton } from '../common/MagneticButton';
import { Play, RotateCcw, Copy, Check, Terminal, Zap, Timer, Cpu } from 'lucide-react';

export const GroqSpeedPlayground: React.FC = () => {
  const [selectedPresetIndex, setSelectedPresetIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeSpeed, setActiveSpeed] = useState(542);
  const [activeTtft, setActiveTtft] = useState(14.8);
  const streamIntervalRef = useRef<number | null>(null);

  const currentPreset = PLAYGROUND_PRESETS[selectedPresetIndex];

  // Streaming Engine: Simulates 500+ tokens/sec output
  const runStream = (textToStream: string, targetTokensSec: number, ttft: number) => {
    if (streamIntervalRef.current) {
      clearInterval(streamIntervalRef.current);
    }

    setDisplayText('');
    setIsStreaming(true);
    setActiveSpeed(targetTokensSec);
    setActiveTtft(ttft);

    // Initial simulated TTFT delay (e.g., 15-20ms)
    setTimeout(() => {
      let charIndex = 0;
      const chunkSize = 12; // Stream in high-speed character blocks (mirrors 500+ tok/s)
      const intervalDelay = 18; // ~55 frames/sec of token updates

      streamIntervalRef.current = window.setInterval(() => {
        if (charIndex >= textToStream.length) {
          if (streamIntervalRef.current) clearInterval(streamIntervalRef.current);
          setIsStreaming(false);
          return;
        }

        charIndex = Math.min(textToStream.length, charIndex + chunkSize);
        setDisplayText(textToStream.substring(0, charIndex));
      }, intervalDelay);
    }, ttft);
  };

  // Run stream when preset changes
  useEffect(() => {
    runStream(currentPreset.response, currentPreset.tokensPerSec, currentPreset.ttftMs);
    return () => {
      if (streamIntervalRef.current) clearInterval(streamIntervalRef.current);
    };
  }, [selectedPresetIndex]);

  const handleCopy = () => {
    navigator.clipboard.writeText(displayText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section
      id="playground"
      className="relative py-16 sm:py-24 md:py-32 px-4 sm:px-6 max-w-7xl mx-auto z-10"
      aria-label="Live Speed Inference Playground"
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 sm:mb-12 pb-4 sm:pb-6 border-b border-white/10 gap-6">
        <div>
          <div className="flex items-center gap-2.5 text-xs font-mono text-sunset-flame tracking-ultra uppercase mb-3">
            <PixelGlyph name="pulse-dot" size={12} />
            <span>LIVE INTERACTIVE SIMULATOR // SUB-MILLISECOND STREAM ENGINE</span>
          </div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl md:text-5xl text-cream-100">
            500+ tokens per second. Live.
          </h2>
        </div>
        <p className="max-w-md text-xs sm:text-sm text-cream-muted leading-relaxed font-mono">
          Experience the physical feeling of instantaneous data streaming. Text and syntax stream at hardware clock speeds with sub-millisecond TTFT.
        </p>
      </div>

      {/* Preset Tabs Selector (Horizontally scrollable on mobile) */}
      <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap no-scrollbar">
        {PLAYGROUND_PRESETS.map((preset, idx) => (
          <button
            key={preset.id}
            onClick={() => setSelectedPresetIndex(idx)}
            className={`px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-full text-xs font-mono font-medium transition-all duration-200 cursor-pointer shrink-0 ${
              selectedPresetIndex === idx
                ? 'bg-cream-100 text-espresso-950 font-bold shadow-md'
                : 'bg-espresso-900 text-cream-muted border border-white/10 hover:border-white/25 hover:text-cream-100'
            }`}
          >
            {preset.title}
          </button>
        ))}
      </div>

      {/* Terminal Enclosure */}
      <DoubleBezelCard
        tilt={false}
        spotlight={false}
        className="w-full"
        innerClassName="p-0 flex flex-col bg-espresso-950/95"
      >
        {/* Terminal Header & Telemetry Bar */}
        <div className="flex flex-wrap items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b border-white/10 bg-espresso-900/60 gap-3 sm:gap-4">
          {/* Left Title */}
          <div className="flex items-center gap-2.5 sm:gap-3">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500/80" />
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-500/80" />
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-500/80" />
            </div>
            <div className="h-4 w-px bg-white/15" />
            <div className="flex items-center gap-2 text-xs font-mono text-cream-200">
              <Terminal className="w-3.5 h-3.5 text-sunset-gold" />
              <span className="text-[11px] sm:text-xs">TERMINAL // STREAM KINEMATICS ENGINE</span>
            </div>
          </div>

          {/* Right Live Gauges (Speed & TTFT) */}
          <div className="flex items-center gap-3 sm:gap-6 text-xs font-mono">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Zap className="w-3.5 h-3.5 text-sunset-flame animate-pulse" />
              <span className="text-cream-dim text-[10px] sm:text-xs">VELOCITY:</span>
              <span className="text-sunset-flame font-bold text-xs sm:text-sm">
                {activeSpeed} T/S
              </span>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2">
              <Timer className="w-3.5 h-3.5 text-sunset-gold" />
              <span className="text-cream-dim text-[10px] sm:text-xs">TTFT:</span>
              <span className="text-sunset-gold font-bold text-xs sm:text-sm">
                {activeTtft} MS
              </span>
            </div>

            <div className="hidden sm:flex items-center gap-2">
              <Cpu className="w-4 h-4 text-emerald-400" />
              <span className="text-cream-dim">COMPLETED:</span>
              <span className="text-emerald-400 font-bold">
                {isStreaming ? 'STREAMING...' : 'RESOLVED'}
              </span>
            </div>
          </div>
        </div>

        {/* Prompt Input Line */}
        <div className="px-4 py-3 sm:px-6 sm:py-4 bg-espresso-900/30 border-b border-white/5 flex items-start gap-2.5 sm:gap-3 text-xs font-mono text-cream-300">
          <span className="text-sunset-gold font-bold select-none">&gt;&gt;</span>
          <span className="text-cream-100 font-medium leading-relaxed text-[11px] sm:text-xs">
            {currentPreset.prompt}
          </span>
        </div>

        {/* Code / Output Area with Syntax Styling */}
        <div className="p-4 sm:p-6 md:p-8 font-mono text-xs sm:text-sm text-cream-200 leading-relaxed min-h-[260px] sm:min-h-[320px] max-h-[460px] overflow-y-auto no-scrollbar whitespace-pre-wrap selection:bg-sunset-flame selection:text-white">
          {displayText}
          {isStreaming && (
            <span className="inline-block w-2 sm:w-2.5 h-3.5 sm:h-4 ml-1 bg-sunset-gold animate-pulse align-middle" />
          )}
        </div>

        {/* Terminal Footer Controls */}
        <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-t border-white/10 bg-espresso-900/40 text-xs font-mono">
          <div className="flex items-center gap-2.5 sm:gap-3">
            <MagneticButton
              size="sm"
              variant="sunset"
              onClick={() =>
                runStream(currentPreset.response, currentPreset.tokensPerSec, currentPreset.ttftMs)
              }
              showArrow={false}
            >
              <div className="flex items-center gap-1.5">
                <Play className="w-3.5 h-3.5 fill-current" />
                <span>Re-run Stream</span>
              </div>
            </MagneticButton>

            <button
              onClick={() =>
                runStream(currentPreset.response, currentPreset.tokensPerSec, currentPreset.ttftMs)
              }
              className="p-2 rounded-full hover:bg-white/10 text-cream-muted hover:text-cream-100 transition-colors cursor-pointer"
              title="Restart stream"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>

          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md hover:bg-white/10 text-cream-muted hover:text-cream-100 transition-colors cursor-pointer"
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-emerald-400">Copied</span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                <span>Copy</span>
              </>
            )}
          </button>
        </div>
      </DoubleBezelCard>
    </section>
  );
};
